/** A module the browser could not load: after a deploy, the old chunk is gone. */
export function isChunkLoadError(error: unknown): boolean {
  const message = (error as { message?: string } | null)?.message || String(error)
  return message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading CSS chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Failed to load module script') ||
    // Firefox's wording
    message.includes('error loading dynamically imported module')
}

const RELOADED_AT_KEY = 'mac.chunkReloadAt'
/** A second stale-chunk failure this soon after a reload means reloading again would loop. */
const RELOAD_WINDOW_MS = 30_000

/**
 * A full reload fetches the new index.html and its chunks. Returns false, and
 * does not reload, when the page already reloaded for this reason in the last
 * 30 s: a chunk that keeps failing (blocked asset, a cached index ahead of the
 * served chunks) would otherwise reload forever; the caller shows its error.
 * The logger is loaded lazily: this module is imported by the route records,
 * and a static logger import closes an import cycle in the tests that mock the
 * logger through the Pilotage harness (the suite hung on it).
 */
export function reloadForNewVersion(error: unknown): boolean {
  let last = 0
  try { last = Number(sessionStorage.getItem(RELOADED_AT_KEY) || 0) } catch { /* storage blocked */ }
  if (Date.now() - last < RELOAD_WINDOW_MS) return false
  try { sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now())) } catch { /* storage blocked: reload anyway */ }
  void import('@/utils/logger')
    .then(({ logger }) => logger.warn('Chunk loading error detected, reloading page to fetch latest version', error))
    .catch(() => undefined)
  window.location.reload()
  return true
}

/**
 * `router.onError`: a stale chunk reloads (at most once per 30 s); when the
 * reload is refused because it just happened, the member is told the page
 * could not be displayed instead of a click that silently does nothing. Any
 * other router error is logged. Store, i18n and logger are loaded lazily for
 * the same import-cycle reason as above.
 */
export function onRouterError(error: unknown): void {
  if (isChunkLoadError(error)) {
    if (reloadForNewVersion(error)) return
    // Still failing right after a reload: keep the trace, it is the case to diagnose.
    void import('@/utils/logger').then(({ logger }) => logger.error('Router error', error)).catch(() => undefined)
    void Promise.all([import('@/stores/app'), import('@/plugins/i18n')])
      .then(([{ useAppStore }, { default: i18n }]) => {
        const t = i18n.global.t
        useAppStore().toast(t('common.error'), t('referentials.sectionLoadFailed'), 'warn')
      })
      .catch(() => undefined)
    return
  }
  void import('@/utils/logger').then(({ logger }) => logger.error('Router error', error)).catch(() => undefined)
}
